import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";
import type {
  CrmOpportunityCreate,
  CrmOpportunityRead,
  CrmOpportunityUpdate,
  CrmProfileRead,
  CrmProfileUpdate,
} from "./schema";

export type CrmActivityType = "NOTE" | "TASK" | "SIGNATURE" | "DOSSIER";

export interface CrmActivityEvent {
  id: string;
  source_id: string;
  type: CrmActivityType;
  occurred_at: string;
  title: string;
  description: string | null;
  status: string | null;
  href: string;
}

export interface CrmPersonSnippet {
  person_id: string;
  first_name: string;
  last_name: string;
  lifecycle_stage: CrmProfileRead["lifecycle_stage"];
}

export interface CrmPipelineView {
  buckets: Array<{
    stage: CrmOpportunityRead["stage"];
    count: number;
    estimated_value_total_eur: string;
    weighted_value_total_eur: string;
    opportunities: CrmOpportunityRead[];
  }>;
  total_count: number;
  total_value_eur: string;
  weighted_total_eur: string;
}

export interface CrmAttentionView {
  items: Array<{
    person: CrmPersonSnippet;
    severity: "INFO" | "WARNING";
    reasons: string[];
    next_review_date: string | null;
    next_action_label: string | null;
    next_action_date: string | null;
  }>;
  total_count: number;
}

export interface CrmOverdueReviewsView {
  items: Array<{
    person: CrmPersonSnippet;
    next_review_date: string;
    days_overdue: number;
  }>;
  total_count: number;
}

export interface CrmStaleContactsView {
  items: Array<{
    person: CrmPersonSnippet;
    last_contact_date: string | null;
    days_since_contact: number | null;
  }>;
  total_count: number;
}

export const crmApi = {
  listProfiles: () => api.get<CrmProfileRead[]>("/crm/profiles"),
  getProfile: (personId: string) => api.get<CrmProfileRead>(`/crm/profiles/${personId}`),
  updateProfile: (personId: string, payload: CrmProfileUpdate) =>
    api.put<CrmProfileRead>(`/crm/profiles/${personId}`, payload),
  activity: (personId: string, params?: { limit?: number; offset?: number }) =>
    api.get<CrmActivityEvent[]>(
      `/crm/profiles/${personId}/activity`,
      params as Record<string, number> | undefined,
    ),
  listOpportunities: () => api.get<CrmOpportunityRead[]>("/crm/opportunities"),
  personOpportunities: (personId: string) =>
    api.get<CrmOpportunityRead[]>(`/crm/profiles/${personId}/opportunities`),
  createOpportunity: (personId: string, payload: CrmOpportunityCreate) =>
    api.post<CrmOpportunityRead>(`/crm/profiles/${personId}/opportunities`, payload),
  updateOpportunity: (opportunityId: string, payload: CrmOpportunityUpdate) =>
    api.patch<CrmOpportunityRead>(`/crm/opportunities/${opportunityId}`, payload),
  deleteOpportunity: (opportunityId: string) =>
    api.delete<void>(`/crm/opportunities/${opportunityId}`),
  pipelineView: () => api.get<CrmPipelineView>("/crm/views/pipeline"),
  attentionView: () => api.get<CrmAttentionView>("/crm/views/attention"),
  overdueReviewsView: () => api.get<CrmOverdueReviewsView>("/crm/views/overdue-reviews"),
  staleContactsView: () => api.get<CrmStaleContactsView>("/crm/views/stale-contacts"),
};

export const crmKeys = {
  all: ["crm"] as const,
  profiles: () => [...crmKeys.all, "profiles"] as const,
  profile: (personId: string) => [...crmKeys.profiles(), personId] as const,
  activity: (personId: string) => [...crmKeys.profile(personId), "activity"] as const,
  opportunities: () => [...crmKeys.all, "opportunities"] as const,
  personOpportunities: (personId: string) =>
    [...crmKeys.opportunities(), "person", personId] as const,
  views: () => [...crmKeys.all, "views"] as const,
  view: (name: string) => [...crmKeys.views(), name] as const,
};

export function useCrmProfiles() {
  return useQuery({
    queryKey: crmKeys.profiles(),
    queryFn: crmApi.listProfiles,
  });
}

export function useCrmProfile(personId: string | undefined) {
  return useQuery({
    queryKey: crmKeys.profile(personId ?? ""),
    queryFn: () => crmApi.getProfile(personId!),
    enabled: !!personId,
  });
}

export function useUpdateCrmProfile(personId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CrmProfileUpdate) => crmApi.updateProfile(personId, payload),
    onSuccess: (profile) => {
      queryClient.setQueryData(crmKeys.profile(personId), profile);
      void queryClient.invalidateQueries({ queryKey: crmKeys.profiles() });
    },
  });
}

export function useCrmActivity(personId: string | undefined) {
  return useQuery({
    queryKey: crmKeys.activity(personId ?? ""),
    queryFn: () => crmApi.activity(personId!, { limit: 100 }),
    enabled: !!personId,
  });
}

export function useCrmOpportunities() {
  return useQuery({
    queryKey: crmKeys.opportunities(),
    queryFn: crmApi.listOpportunities,
  });
}

export function useCrmPipelineView() {
  return useQuery({
    queryKey: crmKeys.view("pipeline"),
    queryFn: crmApi.pipelineView,
  });
}

export function useCrmAttentionView() {
  return useQuery({
    queryKey: crmKeys.view("attention"),
    queryFn: crmApi.attentionView,
  });
}

export function useCrmOverdueReviewsView() {
  return useQuery({
    queryKey: crmKeys.view("overdue-reviews"),
    queryFn: crmApi.overdueReviewsView,
  });
}

export function useCrmStaleContactsView() {
  return useQuery({
    queryKey: crmKeys.view("stale-contacts"),
    queryFn: crmApi.staleContactsView,
  });
}

export function usePersonCrmOpportunities(personId: string | undefined) {
  return useQuery({
    queryKey: crmKeys.personOpportunities(personId ?? ""),
    queryFn: () => crmApi.personOpportunities(personId!),
    enabled: !!personId,
  });
}

function invalidateOpportunities(queryClient: ReturnType<typeof useQueryClient>, personId: string) {
  void queryClient.invalidateQueries({ queryKey: crmKeys.opportunities() });
  void queryClient.invalidateQueries({ queryKey: crmKeys.personOpportunities(personId) });
}

export function useCreateCrmOpportunity(personId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CrmOpportunityCreate) => crmApi.createOpportunity(personId, payload),
    onSuccess: () => invalidateOpportunities(queryClient, personId),
  });
}

export function useUpdateCrmOpportunity(personId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      opportunityId,
      payload,
    }: {
      opportunityId: string;
      payload: CrmOpportunityUpdate;
    }) => crmApi.updateOpportunity(opportunityId, payload),
    onSuccess: () => invalidateOpportunities(queryClient, personId),
  });
}

export function useDeleteCrmOpportunity(personId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: crmApi.deleteOpportunity,
    onSuccess: () => invalidateOpportunities(queryClient, personId),
  });
}
